import { parse } from 'node-html-parser'

export type ConditionMatch = 'all' | 'any'
export type ConditionSourceType = 'native_field' | 'custom_field' | 'tag' | 'segment' | 'unknown'
export type ConditionOperator = '==' | '!=' | 'contains' | '>' | '<' | '>=' | '<='
export type ConditionValueType = 'string' | 'number' | 'boolean'

export type ConditionRule = {
  sourceType: ConditionSourceType
  label: string
  token: string
  operator: ConditionOperator
  value: string | number | boolean
}

export type ConditionalBranch = {
  match: ConditionMatch
  rules: ConditionRule[]
  content: string
}

export type LegacyCondition = {
  property?: string
  operator?: ConditionOperator
  value?: string | number | boolean
  content?: string
}

type ParsedLiquidBlock = {
  html: string
  endIndex: number
}

type TopLevelTag = {
  type: 'if' | 'elsif' | 'else' | 'endif'
  raw: string
  start: number
  end: number
  expression: string
}

const BLOCK_TAG_REGEX = /<(p|h1|h2|h3|h4|h5|h6|ul|ol|li|div|blockquote|pre|table|tr|td|th)\b/i

export function migrateLegacyConditionsToBranches(conditions: LegacyCondition[] = []): ConditionalBranch[] {
  return conditions
    .filter((condition) => condition?.property && condition.value !== undefined)
    .map((condition) => ({
      match: 'all' as const,
      rules: [
        {
          sourceType: 'segment' as const,
          label: String(condition.property),
          token: String(condition.property),
          operator: condition.operator || '==',
          value: condition.value as string | number | boolean
        }
      ],
      content: condition.content || ''
    }))
}

export function legacyConditionsFromBranches(branches: ConditionalBranch[] = []): LegacyCondition[] {
  return branches.map((branch) => {
    const firstRule = branch.rules[0]
    return {
      property: firstRule?.token || '',
      operator: firstRule?.operator || '==',
      value: firstRule?.value ?? '',
      content: branch.content || ''
    }
  })
}

export function supportedBranchCount(branches: ConditionalBranch[] = []): number {
  return branches.filter((branch) => branch.rules.length > 0).length
}

export function parseConditionExpression(expression: string): Omit<ConditionalBranch, 'content'> | null {
  const normalized = expression.trim()

  if (!normalized || /[()]/.test(normalized) || /&&|\|\|/.test(normalized)) {
    return null
  }

  const operator = detectLogicalOperator(normalized)
  if (!operator) return null

  const parts = splitExpression(normalized, operator.logical)
  if (parts.length === 0) return null

  const rules: ConditionRule[] = []
  for (const part of parts) {
    const rule = parseConditionRule(part)
    if (!rule) return null
    rules.push(rule)
  }

  return {
    match: operator.match,
    rules
  }
}

function detectLogicalOperator(expression: string): { logical: 'and' | 'or' | null; match: ConditionMatch } | null {
  const hasAnd = hasTopLevelLogicalOperator(expression, 'and')
  const hasOr = hasTopLevelLogicalOperator(expression, 'or')

  if (hasAnd && hasOr) return null
  if (hasAnd) return { logical: 'and', match: 'all' }
  if (hasOr) return { logical: 'or', match: 'any' }
  return { logical: null, match: 'all' }
}

function hasTopLevelLogicalOperator(expression: string, operator: 'and' | 'or'): boolean {
  let quote: string | null = null
  const needle = ` ${operator} `

  for (let i = 0; i < expression.length; i++) {
    const char = expression[i]
    if (quote) {
      if (char === quote && expression[i - 1] !== '\\') quote = null
      continue
    }
    if (char === '"' || char === "'") {
      quote = char
      continue
    }
    if (expression.slice(i, i + needle.length).toLowerCase() === needle) {
      return true
    }
  }

  return false
}

function splitExpression(expression: string, logical: 'and' | 'or' | null): string[] {
  if (!logical) return [expression.trim()]

  const parts: string[] = []
  let quote: string | null = null
  let buffer = ''
  const needle = ` ${logical} `

  for (let i = 0; i < expression.length; i++) {
    const char = expression[i]
    if (quote) {
      buffer += char
      if (char === quote && expression[i - 1] !== '\\') quote = null
      continue
    }
    if (char === '"' || char === "'") {
      quote = char
      buffer += char
      continue
    }
    if (expression.slice(i, i + needle.length).toLowerCase() === needle) {
      if (!buffer.trim()) return []
      parts.push(buffer.trim())
      buffer = ''
      i += needle.length - 1
      continue
    }
    buffer += char
  }

  if (buffer.trim()) parts.push(buffer.trim())
  return parts
}

function parseConditionRule(expression: string): ConditionRule | null {
  const comparisonMatch = /^([\w.]+)\s*(==|!=|>=|<=|>|<)\s*(.+)$/.exec(expression.trim())
  if (comparisonMatch) {
    const value = parseLiteral(comparisonMatch[3])
    if (value === null) return null
    return ruleFromParts(comparisonMatch[1], comparisonMatch[2] as ConditionOperator, value)
  }

  const containsMatch = /^([\w.]+)\s+contains\s+(.+)$/.exec(expression.trim())
  if (containsMatch) {
    const value = parseLiteral(containsMatch[2])
    if (value === null) return null
    return ruleFromParts(containsMatch[1], 'contains', value)
  }

  return null
}

function ruleFromParts(token: string, operator: ConditionOperator, value: string | number | boolean): ConditionRule {
  return {
    sourceType: token.endsWith('.tags') ? 'tag' : 'unknown',
    label: token,
    token,
    operator,
    value
  }
}

function parseLiteral(rawValue: string): string | number | boolean | null {
  const trimmed = rawValue.trim()
  const quoted = /^(?:"((?:[^"\\]|\\.)*)"|'((?:[^'\\]|\\.)*)')$/.exec(trimmed)
  if (quoted) return unescapeLiquidString(quoted[1] ?? quoted[2] ?? '')
  if (/^-?\d+(?:\.\d+)?$/.test(trimmed)) return Number(trimmed)
  if (trimmed === 'true') return true
  if (trimmed === 'false') return false
  return null
}

function unescapeLiquidString(value: string): string {
  return value.replace(/\\(["'\\])/g, '$1')
}

function escapeLiquidString(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')
}

export function serializeBranchExpression(branch: ConditionalBranch): string {
  const joiner = branch.match === 'any' ? ' or ' : ' and '
  return branch.rules.map(serializeRule).join(joiner)
}

function serializeRule(rule: ConditionRule): string {
  const value = serializeLiteral(rule.value)
  if (rule.operator === 'contains') {
    return `${rule.token} contains ${value}`
  }
  return `${rule.token} ${rule.operator} ${value}`
}

function serializeLiteral(value: string | number | boolean): string {
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return `"${escapeLiquidString(String(value))}"`
}

export function detectConditionValueType(value: string | number | boolean): ConditionValueType {
  if (typeof value === 'number') return 'number'
  if (typeof value === 'boolean') return 'boolean'
  return 'string'
}

export function normalizeConditionRuleValue(
  value: string | number | boolean,
  valueType: ConditionValueType
): string | number | boolean {
  if (valueType === 'number') {
    if (typeof value === 'number') return value
    if (typeof value === 'string' && /^-?\d+(?:\.\d+)?$/.test(value.trim())) return Number(value)
    return value
  }

  if (valueType === 'boolean') {
    if (typeof value === 'boolean') return value
    if (value === 'true') return true
    if (value === 'false') return false
    return value
  }

  return typeof value === 'string' ? value : String(value)
}

export function displayConditionRuleField(rule: Pick<ConditionRule, 'label' | 'token'>): string {
  return rule.token || rule.label || 'field'
}

export function conditionalHtmlToLiquid(html = ''): string {
  const root = parse(html)
  let result = processHtmlNode(root)
  result = result.replace(/<p>\s*<\/p>/g, '')

  const parsedResult = parse(result)
  const paragraphs = parsedResult.querySelectorAll('p')
  if (paragraphs.length === 1 && parsedResult.childNodes.length === 1) {
    result = paragraphs[0].innerHTML
  }

  return result
}

function processHtmlNode(node: any): string {
  if (node.nodeType === 3) return node.rawText ?? node.text ?? ''

  if (node.nodeType !== 1 && node.nodeType !== 9) return ''

  if (node.nodeType === 1 && node.hasAttribute?.('data-conditional')) {
    return serializeConditionalElement(node)
  }

  let content = ''
  for (const child of node.childNodes || []) {
    content += processHtmlNode(child)
  }

  if (node.nodeType !== 1) return content
  if (node.hasAttribute?.('data-phrase-id')) return `{{ phrases.${node.getAttribute('data-phrase-id')} }}`

  const tagName = String(node.tagName || '').toUpperCase()
  if (tagName === 'P') return content.trim() ? `<p>${content}</p>` : ''
  if (['H1', 'H2', 'H3', 'H4', 'H5', 'H6'].includes(tagName))
    return `<${tagName.toLowerCase()}>${content}</${tagName.toLowerCase()}>`
  if (tagName === 'UL') return `<ul>${content}</ul>`
  if (tagName === 'OL') return `<ol>${content}</ol>`
  if (tagName === 'LI') return `<li>${content}</li>`
  if (tagName === 'A') return `<a href="${escapeHtmlAttribute(node.getAttribute('href') || '')}">${content}</a>`
  if (['STRONG', 'B'].includes(tagName)) return `<strong>${content}</strong>`
  if (['EM', 'I'].includes(tagName)) return `<em>${content}</em>`
  if (tagName === 'U') return `<u>${content}</u>`
  if (tagName === 'S') return `<s>${content}</s>`
  if (tagName === 'BR') return '<br>'

  return content
}

function serializeConditionalElement(node: any): string {
  const branches: ConditionalBranch[] = []
  let elseContent = ''

  for (const child of node.childNodes || []) {
    if (child.nodeType !== 1) continue
    if (child.hasAttribute?.('data-condition')) {
      const branch = branchFromConditionElement(child)
      if (!branch || branch.rules.length === 0) return node.toString()
      branches.push({ ...branch, content: processChildren(child) })
    } else if (child.hasAttribute?.('data-condition-else')) {
      elseContent = processChildren(child)
    }
  }

  if (branches.length === 0) return elseContent

  let liquid = ''
  branches.forEach((branch, index) => {
    liquid += `{% ${index === 0 ? 'if' : 'elsif'} ${serializeBranchExpression(branch)} %}`
    liquid += branch.content
  })
  if (elseContent) liquid += `{% else %}${elseContent}`
  liquid += '{% endif %}'
  return liquid
}

function processChildren(node: any): string {
  let content = ''
  for (const child of node.childNodes || []) content += processHtmlNode(child)
  return content
}

export function branchFromConditionElement(element: Element | any): ConditionalBranch | null {
  const encodedRules = element.getAttribute?.('data-condition-rules')
  if (encodedRules) {
    try {
      const parsed = JSON.parse(decodeHtmlAttribute(encodedRules))
      if (Array.isArray(parsed)) {
        return {
          match: element.getAttribute('data-condition-match') === 'any' ? 'any' : 'all',
          rules: parsed.filter(isConditionRule),
          content: element.innerHTML || ''
        }
      }
    } catch {
      return null
    }
  }

  const property = element.getAttribute?.('data-condition-property')
  const value = element.getAttribute?.('data-condition-value')
  if (!property || value === null || value === undefined) return null

  return {
    match: 'all',
    rules: [
      {
        sourceType: 'segment',
        label: property,
        token: property,
        operator: (element.getAttribute('data-condition-operator') || '==') as ConditionOperator,
        value
      }
    ],
    content: element.innerHTML || ''
  }
}

function isConditionRule(value: any): value is ConditionRule {
  return Boolean(
    value &&
      typeof value.token === 'string' &&
      typeof value.operator === 'string' &&
      ['==', '!=', 'contains', '>', '<', '>=', '<='].includes(value.operator)
  )
}

export function liquidConditionalsToHtml(liquid = '', createId: () => string): string {
  if (!liquid) return ''
  return processLiquidRange(liquid, 0, liquid.length, createId)
}

function processLiquidRange(template: string, start: number, end: number, createId: () => string): string {
  let output = ''
  let cursor = start

  while (cursor < end) {
    const ifIndex = findNextIfTag(template, cursor, end)
    if (ifIndex === -1) {
      output += template.slice(cursor, end)
      break
    }

    output += template.slice(cursor, ifIndex)
    const block = parseLiquidBlock(template, ifIndex, end, createId)
    if (!block) {
      const tagEnd = template.indexOf('%}', ifIndex)
      if (tagEnd === -1 || tagEnd + 2 > end) {
        output += template.slice(ifIndex, end)
        break
      }
      output += template.slice(ifIndex, tagEnd + 2)
      cursor = tagEnd + 2
      continue
    }

    output += block.html
    cursor = block.endIndex
  }

  return output
}

function findNextIfTag(template: string, start: number, end: number): number {
  const tagRegex = /{%-?\s*if\b/g
  tagRegex.lastIndex = start
  const match = tagRegex.exec(template)
  return match && match.index < end ? match.index : -1
}

function parseLiquidBlock(
  template: string,
  start: number,
  limit: number,
  createId: () => string
): ParsedLiquidBlock | null {
  const liquidTagRegex = /{%-?\s*(if|elsif|else|endif)\b([\s\S]*?)-?%}/g
  liquidTagRegex.lastIndex = start
  let depth = 0
  let nested = false
  const topLevelTags: TopLevelTag[] = []
  let endIndex = -1
  let match: RegExpExecArray | null

  while ((match = liquidTagRegex.exec(template)) !== null && match.index < limit) {
    const type = match[1] as TopLevelTag['type']
    const expression = match[2].trim().replace(/^-/, '').trim()

    if (type === 'if') {
      if (depth === 0 && match.index !== start) return null
      if (depth > 0) nested = true
      depth++
      if (depth === 1)
        topLevelTags.push({ type, raw: match[0], start: match.index, end: liquidTagRegex.lastIndex, expression })
      continue
    }

    if (depth === 0) return null

    if (type === 'elsif' || type === 'else') {
      if (depth === 1)
        topLevelTags.push({ type, raw: match[0], start: match.index, end: liquidTagRegex.lastIndex, expression })
      continue
    }

    if (type === 'endif') {
      depth--
      if (depth === 0) {
        topLevelTags.push({ type, raw: match[0], start: match.index, end: liquidTagRegex.lastIndex, expression })
        endIndex = liquidTagRegex.lastIndex
        break
      }
    }
  }

  if (endIndex === -1) return null
  const rawBlock = template.slice(start, endIndex)
  if (nested) return { html: rawBlock, endIndex }

  const branches: ConditionalBranch[] = []
  let elseContent = ''
  for (let i = 0; i < topLevelTags.length; i++) {
    const tag = topLevelTags[i]
    if (tag.type === 'endif') break
    const nextTag = topLevelTags[i + 1]
    if (!nextTag) return { html: rawBlock, endIndex }

    if (tag.type === 'if' || tag.type === 'elsif') {
      const parsed = parseConditionExpression(tag.expression)
      if (!parsed || parsed.rules.length === 0) return { html: rawBlock, endIndex }
      branches.push({ ...parsed, content: template.slice(tag.end, nextTag.start) })
    } else if (tag.type === 'else') {
      elseContent = template.slice(tag.end, nextTag.start)
    }
  }

  if (branches.length === 0) return { html: rawBlock, endIndex }
  return { html: renderConditionalHtml(createId(), branches, elseContent), endIndex }
}

export function renderConditionalHtml(conditionalId: string, branches: ConditionalBranch[], elseHtml = ''): string {
  const hasBlock =
    branches.some((branch) => BLOCK_TAG_REGEX.test(branch.content || '')) || BLOCK_TAG_REGEX.test(elseHtml || '')
  const wrapperTag = hasBlock ? 'div' : 'span'
  const segmentTag = hasBlock ? 'div' : 'span'
  let html = `<${wrapperTag} data-conditional="${escapeHtmlAttribute(conditionalId)}">`

  for (const branch of branches) {
    const attrs = conditionElementAttributes(branch)
    html += `<${segmentTag}${attrs}>${branch.content || ''}</${segmentTag}>`
  }
  if (elseHtml) html += `<${segmentTag} data-condition-else>${elseHtml}</${segmentTag}>`
  html += `</${wrapperTag}>`
  return html
}

export function conditionElementAttributes(branch: ConditionalBranch): string {
  const firstRule = branch.rules[0]
  const attrs = [
    'data-condition',
    `data-condition-match="${branch.match}"`,
    `data-condition-rules="${escapeHtmlAttribute(JSON.stringify(branch.rules))}"`
  ]

  if (firstRule) {
    attrs.push(`data-condition-property="${escapeHtmlAttribute(firstRule.token)}"`)
    attrs.push(`data-condition-value="${escapeHtmlAttribute(String(firstRule.value))}"`)
    if (firstRule.operator !== '==') attrs.push(`data-condition-operator="${escapeHtmlAttribute(firstRule.operator)}"`)
  }

  return ` ${attrs.join(' ')}`
}

function escapeHtmlAttribute(value: string): string {
  return String(value).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

function decodeHtmlAttribute(value: string): string {
  return String(value)
    .replace(/&quot;/g, '"')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
}
